import React, { useCallback } from 'react';

import {
  PieceJointeTypeResponseDto,
  UploadPieceJointeViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import BaseForm from 'components/BaseForm/BaseForm';
import AsyncSelect from 'components/BaseForm/Select/AsyncSelect';
import { CSSUtils } from 'utils/CSSUtils';

import useFormatBytes from '../../../utils/useFormatByte';
import BaseUploader, {
  uploaderFullWidthColProps,
} from '../../BaseForm/BaseUploader';
import WarningMessage from '../../WarningMessage/WarningMessage';

import { fileTypes, maxFileSize } from './utils/constants';
import { getInitialsValues } from './utils/mappers';
import { fetchPieceJointeTypes } from './utils/searchPieceJointeTypes';
import {
  Controller,
  SetDossier,
  UploadPieceJointeFormValues,
} from './utils/types';
import useSubmitUploadPieceJointe from './utils/useSubmitUploadPieceJointe';
import { validateUploadPieceJointe } from './utils/validateUploadPieceJointe';

import './ImporterPieceJointeBase.less';

interface ImporterPieceJointeProps {
  closeModal: () => void;
  dossierId: string;
  controller: Controller;
  setDossier: SetDossier;
}

function ImporterPieceJointeBase({
  closeModal,
  dossierId,
  controller,
  setDossier,
}: ImporterPieceJointeProps): React.ReactElement {
  const { submit, inProgress } = useSubmitUploadPieceJointe(
    controller,
    dossierId,
    setDossier,
    closeModal,
  );

  const formatBytes = useFormatBytes(+maxFileSize);

  const validateField = useCallback(
    async (
      field: UploadPieceJointeViolationDtoFormFieldEnum,
      formValues: UploadPieceJointeFormValues,
    ) => {
      return validateUploadPieceJointe(
        field,
        dossierId,
        formValues,
        controller,
      );
    },
    [dossierId, controller],
  );

  const warningDetails = [
    'Vous pouvez joindre un seul document de chaque type',
    'Les documents seront disponibles pour tous les utilisateurs ayant accès au dossier',
    'Les documents ne peuvent pas être supprimés',
    `La taille maximale est de ${formatBytes()} par document`,
  ];

  return (
    <>
      <h3>Joindre un document</h3>
      <WarningMessage title="À savoir" detail={warningDetails} isList />
      <BaseForm
        onSubmit={submit}
        onCancel={closeModal}
        isSubmitting={inProgress}
        initialValues={getInitialsValues()}
        labelCol={{ span: CSSUtils.LlargeWrapperCol }}
        wrapperCol={{ span: CSSUtils.mediumLargeWrapperCol }}
        validateField={validateField}
        inputs={[
          {
            field: UploadPieceJointeViolationDtoFormFieldEnum.TYPE,
            required: true,
            name: 'type',
            label: 'Type',
            render: () => {
              return (
                <AsyncSelect
                  fetchOptions={() => {
                    return fetchPieceJointeTypes(controller, dossierId);
                  }}
                  filterOptions={(_: UploadPieceJointeFormValues, options) => {
                    return options.filter(option => {
                      return option.disponible;
                    });
                  }}
                  getOptionValue={(type: PieceJointeTypeResponseDto) =>
                    type.identifiant
                  }
                  getOptionLabel={(type: PieceJointeTypeResponseDto) =>
                    type.libelle
                  }
                />
              );
            },
          },
          {
            field: UploadPieceJointeViolationDtoFormFieldEnum.FILE,
            name: 'file',
            label: 'File',
            ...uploaderFullWidthColProps,
            render: () => {
              return (
                <BaseUploader
                  acceptedFilesTypes={fileTypes.split(',')}
                  maxFileSize={+maxFileSize}
                />
              );
            },
          },
        ]}
        renderButtonAction={(Submit, Cancel) => {
          return (
            <div className="UploadPieceJointeActions">
              {Cancel}
              {Submit}
            </div>
          );
        }}
      />
    </>
  );
}

export default ImporterPieceJointeBase;
